import React from "react";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import WidgetTitle from "components/widgetParts/WidgetTitle";
import { defaultSettings } from "./SingleBarSettings";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getOriginalRequestByNameFromResponse, getSingleWidgetResponseByName } from "system/widget/Widget";

function SingleBarChart(props: WithQueryWidgetProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'single-bar-chart');
  const apiResponse = apiResponseInfo?.responseBody;

  const requestBody = getOriginalRequestByNameFromResponse(props.apiResponses, 'single-bar-chart')?.requestInit?.body || {};
  const aggs = requestBody?.aggs ?? {};

  const requestAgg = Object.keys(aggs)[0];
  const row = apiResponse?.[requestAgg] || [];
  const label = row?.labels?.[0];
  const percentage = row?.series?.[1]?.values?.[0];
  const fillWidth = `${(percentage * 100).toFixed(2)}%`;
  const currentState = {...defaultSettings, ...props.widget?.data?.settings};
  const titleAlignment = currentState.widgetTitleAlignment;
  const showDataValue = currentState.showDataValue ?? true;

  return (
    <>
      {percentage &&
        <Stack
          sx={{
            height: "100%",
            padding: 2
          }}>
          <Stack
            direction={titleAlignment === 'right' ? 'row-reverse' : 'row'}
            sx={{
              justifyContent: titleAlignment === 'center' ? 'center' : 'space-between'
            }}>
            {(currentState.widgetTitle !== 'none') &&
              <WidgetTitle
                alignment={titleAlignment}
                customTitle={currentState.customWidgetTitle}
                defaultTitle={row?.keys[0] === null ? `${i18n.t("null")}` : label}
                settingsTitle={currentState.widgetTitle}
                style={{ padding: 0 }}
                widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
            }
            {showDataValue &&
              <Typography
                variant="subtitle2"
                sx={{
                  marginLeft: titleAlignment === 'center' ? 1 : 0,
                  fontSize: "1.2rem"
                }}>
                {fillWidth}
              </Typography>}
          </Stack>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: theme.palette.grey[300]
            }}>
            <Tooltip title={fillWidth}>
              <Box
                sx={{
                  height: "100%",
                  width: fillWidth,
                  backgroundColor: currentState.fillColor || apiResponseInfo?.originatingRequest?.queryRequestColor || theme.palette.primary.main
                }} />
            </Tooltip>
          </Box>
        </Stack>
      }
      {!percentage &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(SingleBarChart, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});